// src/app/services/fanApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import {
  IFan,
  IFanCurve,
  IFanIsolatorsSpringParams,
  IFanIspEspApd,
  IFanModel,
  IFanMotorBaseParams,
  IFanNotes,
  IFanParams,
  IFanSettings,
  IIdNameValue,
  ISelectedMotor,
  UpdatePayload,
} from '../models';

export const fanApi = createApi({
  reducerPath: 'fanApi',
  baseQuery,
  endpoints: (builder) => ({
    getFanParams: builder.query<{ fan: IFanParams }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/fans/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFanMotorBaseParams: builder.query<
      { motor_base: IFanMotorBaseParams },
      void
    >({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/motors/bases/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFanIsolatorsSpringParams: builder.query<
      { spring_isolator: IFanIsolatorsSpringParams },
      void
    >({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/fans/isolators/spring/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getSystemEffect: builder.query<IIdNameValue[], void>({
      query: () =>
        `/api/v1.0/cms/1/v1.0.0/ahu/fans/params/system_effect/?fan_type_id=4`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFanMountings: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/fan_mountings/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFanSpacingSides: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/fan/spacing/sides/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFanSpacingTopBtm: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/fan/spacing/top_btm/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFan: builder.query<IFan, number>({
      query: (id) => `/api/v1.0/ahu/fans/${id}`,
      transformResponse: (data: IFan) => {
        if (!data.master_fan_id && !data.discharge_id) {
          data.discharge_id = 2;
        }
        return data;
      },
    }),
    getFanSettings: builder.query<IFanSettings, { type: string; arr: string }>({
      query: ({ type, arr }) => ({
        url: `api/v1.0/cms/1/v1.0.0/ahu/fans/${type}_arr${arr}/mfg/settings/`,
      }),
      extraOptions: {
        cacheData: true,
      },
      transformResponse: (data: IFanSettings) => {
        const wide_arr = [];
        const hige_arr = [];
        for (let i = 1; i <= data.max_qty_wide; i++) {
          wide_arr.push(i);
        }
        for (let i = 1; i <= data.max_qty_high; i++) {
          hige_arr.push(i);
        }
        data.wide_arr = wide_arr;
        data.hige_arr = hige_arr;
        return {
          ...data,
          wide_arr,
          hige_arr,
        };
      },
    }),
    getMotorsCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/motors/all/count/${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getFanMotors: builder.query<any, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/motors/all/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getFanModel: builder.query<IFanModel[], string>({
      query: (queryString) => `/api/v1.0/cms/1/v1.0.0/ahu/fans/${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getSelectedMotor: builder.query<
      ISelectedMotor,
      { motor_id: number; voltage_id: number }
    >({
      query: ({ motor_id, voltage_id }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/motors/all/${motor_id}?voltage_id=${voltage_id}`,
    }),
    getFanNotes: builder.query<IFanNotes[], number>({
      query: (id) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/fans/notes/?component_mfg_id=${id}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getFanIsp: builder.mutation<IFanIspEspApd, { id: number; body: any }>({
      query: ({ id, body }) => ({
        url: `/api/v1.0/ahu/fans/${id}/isp/`,
        method: 'POST',
        body,
      }),
    }),
    // PATCH update fan by ID
    updateFan: builder.mutation<IFan, UpdatePayload<IFan>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/fans/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
      transformResponse: (data: IFan) => {
        if (!data.master_fan_id && !data.discharge_id) {
          data.discharge_id = 2;
        }
        return data;
      },
    }),
    updateFanCurve: builder.mutation<IFanCurve, UpdatePayload<IFanCurve>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/fans/curves/${id}`,
        method: 'PUT',
        body,
        signal,
      }),
    }),
    createFan: builder.mutation<any, UpdatePayload<{ section_id: number }>>({
      query: ({ body, signal }) => ({
        url: `/api/v1.0/ahu/fans/`,
        method: 'POST',
        body,
        signal,
      }),
    }),
  }),
});

export const {
  useLazyGetFanQuery,
  useGetFanParamsQuery,
  useGetFanMotorBaseParamsQuery,
  useGetFanIsolatorsSpringParamsQuery,
  useGetSystemEffectQuery,
  useGetFanMountingsQuery,
  useLazyGetFanSettingsQuery,
  useLazyGetMotorsCountQuery,
  useLazyGetSelectedMotorQuery,
  useLazyGetFanMotorsQuery,
  useLazyGetFanNotesQuery,
  useLazyGetFanModelQuery,
  useGetFanIspMutation,
  useGetFanSpacingSidesQuery,
  useGetFanSpacingTopBtmQuery,
  useUpdateFanMutation,
  useUpdateFanCurveMutation,
  useCreateFanMutation,
} = fanApi;